import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, Clock, ExternalLink } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils/utils';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllRead, loading } = useNotifications();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read && !notif.is_virtual) {
      await markAsRead(notif.id);
    }
    setIsOpen(false);
    if (notif.url) {
      navigate(notif.url);
    }
  };

  const handleViewHistory = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative p-2.5 rounded-full border transition-all duration-300 flex items-center justify-center",
          isOpen 
            ? "bg-blue-50 text-[#3a5f9e] border-[#3a5f9e]" 
            : "text-slate-600 border-[#3a5f9e] hover:bg-slate-50 hover:text-[#3a5f9e] active:scale-95"
        )}
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full ring-2 ring-white animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white/95 backdrop-blur-xl border border-white/40 rounded-3xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top-right">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50/50 to-white">
            <h3 className="font-bold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-lg transition-colors"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Clock className="w-8 h-8 animate-spin mx-auto mb-2 opacity-20" />
                <p className="text-sm">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-10" />
                <p className="text-sm font-medium">All caught up!</p>
                <p className="text-xs opacity-60">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={cn(
                      "p-4 cursor-pointer transition-all duration-200 hover:bg-slate-50/80 group relative",
                      !notif.is_read && "bg-blue-50/30"
                    )}
                  >
                    {!notif.is_read && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r-full" />
                    )}
                    <div className="flex gap-3">
                      <div className={cn(
                        "mt-1 w-2 h-2 rounded-full shrink-0",
                        notif.is_read ? "bg-slate-200" : "bg-blue-500"
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2 mb-1">
                          <p className={cn(
                            "text-sm font-bold truncate",
                            notif.is_read ? "text-slate-600" : "text-slate-800"
                          )}>
                            {notif.title}
                          </p>
                          <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                            {formatTime(notif.created_at)}
                          </span>
                        </div>
                        <p className={cn(
                          "text-xs line-clamp-2 leading-relaxed mb-2",
                          notif.is_read ? "text-slate-400" : "text-slate-600"
                        )}>
                          {notif.message}
                        </p>
                        {notif.url && (
                          <div className="flex items-center gap-1 text-[10px] font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            View details <ExternalLink className="w-2.5 h-2.5" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* <div className="p-3 border-t border-slate-100 bg-slate-50/50 text-center">
            <button
              type="button"
              onClick={handleViewHistory}
              className="text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors"
            >
              View History
            </button>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
